import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ShoppingCart } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { useCompare } from "@/lib/compare";
import { api, imgUrl } from "@/lib/api";
import { BrandBadge, StockBadge } from "@/components/shop/Badges";
import { NetPrice } from "@/components/shop/Totals";

const specKey = (s, lang) => (lang !== "lt" ? s.key_en || s.key_lt : s.key_lt || s.key_en);

const Head = ({ p }) => {
  const { t } = useLang();
  const { add } = useCart();
  const { remove } = useCompare();
  return (
    <th className="align-top p-3 text-left font-normal w-[240px] min-w-[220px] h-px">
      <div className="relative flex flex-col h-full">
        <button data-testid={`compare-remove-${p.id}`} onClick={() => remove(p.id)} className="absolute right-1 top-1 z-10 p-1 text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
        <Link to={`/product/${p.id}`} data-testid={`compare-image-${p.id}`} className="relative block h-[200px] w-full overflow-hidden img-stage"><img src={imgUrl(p.images?.[0])} alt={p.name} className="absolute inset-0 w-full h-full object-contain p-4" /></Link>
        <div className="mt-3"><BrandBadge brand={p.brand} /></div>
        <Link to={`/product/${p.id}`} className="block font-display font-bold uppercase leading-tight mt-2 hover:text-orange-500">{p.name}</Link>
        <div className="mt-3 mb-3">{p.price_on_request ? <span className="font-display font-bold text-orange-500">{t("ask_price")}</span>
          : <NetPrice value={p.price} old={p.old_price} className="font-display font-black text-2xl text-orange-500" testid={`compare-price-${p.id}`} />}</div>
        {!p.price_on_request && <button data-testid={`compare-add-${p.id}`} onClick={() => add(p)} className="mt-auto pt-0 translate-y-0 h-9 w-full inline-flex items-center justify-center gap-2 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest text-xs hover:bg-orange-400 transition-colors"><ShoppingCart className="w-4 h-4" />{t("add_cart")}</button>}
      </div>
    </th>
  );
};

export default function ComparePage() {
  const { t, lang } = useLang();
  const { ids, clear } = useCompare();
  const [items, setItems] = useState([]);
  useEffect(() => {
    Promise.all(ids.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null))).then((l) => setItems(l.filter(Boolean)));
  }, [ids]);

  const keys = [...new Set(items.flatMap((p) => (p.specs || []).filter((s) => s.key_lt !== "Tinka").map((s) => specKey(s, lang))))];
  const val = (p, k) => { const s = (p.specs || []).find((x) => specKey(x, lang) === k); return s && (lang !== "lt" ? s.value_en || s.value : s.value); };
  const base = [
    [t("sku"), (p) => p.sku || "—"], [t("machine"), (p) => t(p.machine_type)], [t("suspension"), (p) => t(p.suspension)],
    [t("delivery_time"), (p) => <StockBadge stock={p.stock} />, (p) => p.stock > 0], [t("fits_tractors"), (p) => p.fits || (p.compatible || []).join(" · ") || "—"],
  ];

  return (
    <div data-testid="compare-page" className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
      <div className="flex items-end justify-between gap-4">
        <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight">{t("compare_title")}</h1>
        {items.length > 0 && (
          <div className="flex items-center gap-5">
            <button data-testid="compare-clear-btn" onClick={clear} className="text-sm text-slate-400 hover:text-red-400">{t("compare_clear")}</button>
          </div>
        )}
      </div>
      {!ids.length ? (
        <p data-testid="compare-empty" className="text-slate-400 mt-10">{t("compare_empty")} <Link to="/catalog" className="text-orange-500">{t("nav_catalog")} →</Link></p>
      ) : (
        <div className="overflow-x-auto mt-10 border border-[#3B4157] w-fit max-w-full">
          <table data-testid="compare-table" className="text-sm table-fixed">
            <thead className="border-b border-[#3B4157] bg-[#292D3C]"><tr><th className="sticky left-0 z-20 bg-[#292D3C] w-[150px] min-w-[130px]" />{items.map((p) => <Head key={p.id} p={p} />)}</tr></thead>
            <tbody className="divide-y divide-[#3B4157]">
              {base.map(([label, fn, key]) => {
                const diff = items.length > 1 && new Set(items.map(key || fn)).size > 1;
                return (
                  <tr key={label} data-diff={diff ? "1" : "0"} className={diff ? "bg-orange-500/10" : ""}>
                    <td className={`sticky left-0 z-10 bg-[#1F2230] p-3 ${diff ? "text-orange-400 border-l-2 border-orange-500" : "text-slate-400"}`}>{label}</td>
                    {items.map((p) => <td key={p.id} className={`p-3 ${diff ? "text-orange-300 font-semibold" : ""}`}>{fn(p)}</td>)}
                  </tr>
                );
              })}
              {keys.map((k) => {
                const vals = items.map((p) => val(p, k));
                const diff = new Set(vals.map((v) => v ?? "—")).size > 1;
                return (
                  <tr key={k} data-diff={diff ? "1" : "0"} className={diff ? "bg-orange-500/10" : ""}>
                    <td className={`sticky left-0 z-10 bg-[#1F2230] p-3 ${diff ? "text-orange-400 border-l-2 border-orange-500" : "text-slate-400"}`}>{k}</td>
                    {items.map((p, i) => <td key={p.id} className={`p-3 font-mono-x ${diff ? "text-orange-300 font-semibold" : vals[i] ? "text-slate-200" : "text-slate-600"}`}>{vals[i] || "—"}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
